if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        // No form (e.g. quote-only / offerte products) — nothing to wire up.
        if (!this.form) return;
        if (this.variantIdInput) this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        // No submit button (e.g. sold-out shows a "notify me" button instead) — skip submit wiring.
        if (!this.submitButton) return;
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        // this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        // Optis (BSS Product Options) injects a hidden `__` property with a
        // random value on every submit, which stops Shopify from merging
        // identical lines: the same product added twice shows as two cart
        // lines. Strip it; all real option values (and their addon pricing
        // marker __bss_po_addons) stay on the line.
        formData.delete('properties[__]');
        // Forms marked with data-cart-popup show our custom "toegevoegd" popup
        // instead of the cart drawer / cart-page redirect.
        //   'main'   -> main add: open the popup with the added product.
        //   'silent' -> e.g. the in-popup cross-sell: add + refresh count, keep popup open.
        const popupMode = this.dataset.cartPopup;
        const cartPopupEl = popupMode ? document.querySelector('cart-added-notification') : null;
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        } else if (popupMode) {
          // No cart drawer/notification on the page: still request the header count
          // section so we can refresh it after adding, then show the popup.
          formData.append('sections', 'cart-icon-bubble');
          formData.append('sections_url', window.location.pathname);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            }

            // Product page: add to the Shopify cart, refresh the header count, and
            // show the popup instead of opening the drawer or redirecting to /cart.
            if (popupMode && cartPopupEl) {
              if (!this.error) {
                publish(PUB_SUB_EVENTS.cartUpdate, {
                  source: 'product-form',
                  productVariantId: formData.get('id'),
                  cartData: response,
                });
              }
              this.error = false;
              const bubbleEl = document.getElementById('cart-icon-bubble');
              const bubbleHTML = response.sections && response.sections['cart-icon-bubble'];
              if (bubbleEl && bubbleHTML) {
                const node = new DOMParser().parseFromString(bubbleHTML, 'text/html').querySelector('.shopify-section');
                bubbleEl.innerHTML = node ? node.innerHTML : bubbleHTML;
              }
              // Also refresh the drawer content in place. renderContents() would
              // do this but force-opens the drawer, which the popup replaces —
              // without this, a drawer that rendered empty on page load stays
              // empty until a reload or a re-rendering merge.
              if (this.cart && response.sections && response.sections['cart-drawer']) {
                const drawerEl = document.getElementById('CartDrawer');
                if (drawerEl) {
                  drawerEl.innerHTML = this.cart.getSectionInnerHTML(
                    response.sections['cart-drawer'],
                    '#CartDrawer'
                  );
                  const overlay = this.cart.querySelector('#CartDrawer-Overlay');
                  if (overlay) overlay.addEventListener('click', this.cart.close.bind(this.cart));
                }
              }
              // 'main' opens the popup with the added product; 'silent' (in-popup
              // cross-sell) refreshes the count, leaves the popup open and confirms
              // on the button itself since nothing else visibly changes.
              if (popupMode === 'main' && typeof cartPopupEl.renderItem === 'function') {
                cartPopupEl.renderItem({ title: response.product_title || response.title });
              } else if (popupMode === 'silent') {
                this.showAddedFeedback();
              }
              // Optis appends a random `__` property downstream of this request,
              // splitting identical adds into separate cart lines; fold them
              // back together (see cart-line-merge.js).
              if (window.RotimCartMerge) window.RotimCartMerge.merge();
              return;
            }

            if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            // Fold Optis/Bundler-split duplicate lines back together; re-renders
            // the drawer afterwards when anything changed (cart-line-merge.js).
            if (window.RotimCartMerge) window.RotimCartMerge.merge();
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            // this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      // Swap the submit button's icon for a check for a moment. Used by 'silent'
      // popup adds (the in-popup cross-sell), which otherwise give no visual
      // feedback that the product was added.
      showAddedFeedback() {
        const button = this.submitButton;
        if (!button) return;
        if (!this.addedCheck) {
          this.addedCheck = document.createElement('span');
          this.addedCheck.className = 'product-form__added-check';
          this.addedCheck.setAttribute('aria-hidden', 'true');
          this.addedCheck.innerHTML =
            '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg>';
          button.appendChild(this.addedCheck);
        }
        clearTimeout(this.addedFeedbackTimer);
        button.classList.add('is-added');
        this.addedFeedbackTimer = setTimeout(() => button.classList.remove('is-added'), 2000);
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
